import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageTitle from '../components/PageTitle';

const GetInTouch = () => {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  // Toggle white text on scroll (black at top) like other pages
  useEffect(() => {
    const handleScroll = () => {
      const contactNav = document.getElementById('contact-nav');
      if (!contactNav) return;
      if (window.scrollY > 0) {
        contactNav.classList.add('scrolled');
      } else {
        contactNav.classList.remove('scrolled');
      }
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitStatus(null);
      
      // Console log to verify payload being submitted
      console.log('Form data being submitted:', formData);
      
      try {
        const result = await emailjs.sendForm(
          'service_h4elppd',
          'template_9bdpjog',
          formRef.current,
          'dHEUJ8uZTpS3w5Xvd'
        );
        
        console.log('Email sent successfully:', result);
        setSubmitStatus('success');
        
        // Reset form
        setFormData({
          name: '',
          organization: '',
          email: '',
          phone: '',
          interest: '',
          message: ''
        });
      } catch (error) {
        console.error('Email sending failed:', error);
        setSubmitStatus('error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div style={{ backgroundColor: '#F2F0E6' }}>
      <PageTitle title="Get in Touch" />
      {/* Local navbar wrapper for Contact page */}
      <div id="contact-nav" className="relative z-50">
        <Navbar />
      </div>

      {/* Main Content Section with Background Image */}
      <section 
        className="relative min-h-screen flex items-center justify-center py-20 pt-48"
        style={{
          backgroundImage: "url('/WhatsApp Image 2025-10-01 at 12.45.20 PM 1.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Semi-transparent overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        
        {/* Contact Form Card Container */}
        <div className="relative">
          {/* Custom Close Button - Outside and above the card */}
          <button 
            className="absolute flex items-center justify-center hover:opacity-80 transition-opacity z-10"
            onClick={() => window.history.back()}
            style={{
              width: '48px',
              height: '48px',
              top: '-64px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundImage: "url('/Ellipse 14.png')",
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center'
            }}
          >
            <img 
              src="/x.png" 
              alt="Close" 
              style={{
                width: '17.58px',
                height: '19.86px'
              }}
            />
          </button>
          
          {/* Contact Form Card */}
          <div 
            className="bg-white p-4 md:p-8 mx-4 w-full max-w-md md:max-w-none"
            style={{ 
              width: 'calc(100vw - 2rem)',
              maxWidth: '526px',
              minHeight: '716px',
              borderRadius: '12px',
              boxShadow: '0px 7px 8px 0px #0000001F',
              opacity: 1
            }}
          >

            {/* Title */}
            <h1 
              className="mb-4"
              style={{
                fontFamily: 'Belleza, serif',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '30px',
                lineHeight: '45px',
                letterSpacing: '0%',
                textAlign: 'center',
                textTransform: 'capitalize',
                color: '#272525'
              }}
            >
              Get In Touch
            </h1>

            {/* Introductory Text */}
            <p 
              className="mb-8"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0%',
                textAlign: 'center',
                color: '#272525'
              }}
            >
              We'd love to hear from you. Whether you have a question, need support, or just want to say hello, feel free to reach out.
            </p>

            {/* Success/Error Messages */}
            {submitStatus === 'success' && (
              <div className="w-full max-w-391px mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                <p className="text-center text-sm">
                  Message sent successfully! We'll get back to you soon.
                </p>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="w-full max-w-391px mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                <p className="text-center text-sm">
                  Failed to send message. Please try again or contact us directly.
                </p>
              </div>
            )}

            {/* Contact Form */}
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 flex flex-col items-center">
              {/* Name Field */}
              <div className="w-full flex flex-col items-center">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`px-4 py-3 border-0 rounded focus:outline-none transition-colors ${
                    errors.name ? 'border-red-500' : ''
                  }`}
                  placeholder="Name*"
                  style={{ 
                    width: '100%',
                    maxWidth: '391px',
                    height: '46px',
                    borderRadius: '5px',
                    backgroundColor: '#ACAD960F',
                    color: '#272525'
                  }}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              {/* Organization Field */}
              <div className="w-full flex flex-col items-center">
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  className="px-4 py-3 border-0 rounded focus:outline-none transition-colors"
                  placeholder="Organization"
                  style={{ 
                    width: '100%',
                    maxWidth: '391px',
                    height: '46px',
                    borderRadius: '5px',
                    backgroundColor: '#ACAD960F',
                    color: '#272525'
                  }}
                />
              </div>

              {/* Email Field */}
              <div className="w-full flex flex-col items-center">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`px-4 py-3 border-0 rounded focus:outline-none transition-colors ${
                    errors.email ? 'border-red-500' : ''
                  }`}
                  placeholder="Email*"
                  style={{ 
                    width: '100%',
                    maxWidth: '391px',
                    height: '46px',
                    borderRadius: '5px',
                    backgroundColor: '#ACAD960F',
                    color: '#272525'
                  }}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Phone Number Field */}
              <div className="w-full flex flex-col items-center">
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`px-4 py-3 border-0 rounded focus:outline-none transition-colors ${
                    errors.phone ? 'border-red-500' : ''
                  }`}
                  placeholder="Phone Number*"
                  style={{ 
                    width: '100%',
                    maxWidth: '391px',
                    height: '46px',
                    borderRadius: '5px',
                    backgroundColor: '#ACAD960F',
                    color: '#272525'
                  }}
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}
              </div>

              {/* Interest Field */}
              <div className="w-full flex flex-col items-center">
                <input
                  type="text"
                  id="interest"
                  name="interest"
                  value={formData.interest}
                  onChange={handleInputChange}
                  className="px-4 py-3 border-0 rounded focus:outline-none transition-colors"
                  placeholder="Interest"
                  style={{ 
                    width: '100%',
                    maxWidth: '391px',
                    height: '46px',
                    borderRadius: '5px',
                    backgroundColor: '#ACAD960F',
                    color: '#272525'
                  }}
                />
              </div>

              {/* Message Field */}
              <div className="w-full flex flex-col items-center">
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className={`px-4 py-3 border-0 rounded focus:outline-none transition-colors resize-none ${
                    errors.message ? 'border-red-500' : ''
                  }`}
                  placeholder="Your Message*"
                  style={{ 
                    width: '100%',
                    maxWidth: '391px',
                    height: '120px',
                    borderRadius: '5px',
                    backgroundColor: '#ACAD960F',
                    color: '#272525'
                  }}
                />
                {errors.message && (
                  <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`py-3 px-6 rounded text-white font-semibold transition-opacity ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                }`}
                style={{
                  width: '100%',
                  maxWidth: '391px',
                  height: '46px',
                  borderRadius: '5px',
                  backgroundColor: isSubmitting ? '#ACAD9680' : '#ACAD96',
                  fontFamily: 'Belleza, serif',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  color: '#FFFFFF'
                }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Local CSS overrides for Navbar (Contact page only) */}
      <style jsx>{`
        /* Make navbar overlay the background image like homepage */
        #contact-nav {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 50 !important;
          background: transparent !important;
        }
        
        #contact-nav nav { 
          position: relative; 
          z-index: 50; 
          background: transparent !important;
        }
        
        #contact-nav img[alt="HIK"] { 
          margin-top: 0.5rem !important; 
          position: relative; 
          z-index: 50; 
        }

        @media (min-width: 768px) {
          #contact-nav img[alt="HIK"] { 
            margin-top: 1rem !important; 
          }
        }

        /* Default: white navbar links/buttons over background image */
        #contact-nav .md\\:flex a,
        #contact-nav .md\\:flex button {
          margin-top: 0.5rem !important;
          color: #ffffff !important;
          font-family: Belleza, serif !important;
          font-weight: 400 !important;
          font-size: 20px !important;
          line-height: 100% !important;
        }

        /* Button alignment tweak to match left items */
        #contact-nav .md\\:flex button {
          padding-top: 0.375rem !important;
          padding-bottom: 0.375rem !important;
          position: relative; 
          top: -4px;
        }

        /* When scrolled: keep navbar text white */
        #contact-nav.scrolled .md\\:flex a,
        #contact-nav.scrolled .md\\:flex button {
          color: #ffffff !important;
        }

        /* Placeholder styling */
        input::placeholder,
        textarea::placeholder {
          font-family: Poppins, sans-serif !important;
          font-weight: 400 !important;
          font-style: normal !important;
          font-size: 14px !important;
          line-height: 100% !important;
          letter-spacing: 0% !important;
          color: #0000004D !important;
        }

        /* Mobile responsive adjustments */
        @media (max-width: 640px) {
          #contact-nav {
            padding: 0 1rem !important;
          }
          
          .bg-white {
            margin: 0 1rem !important;
            padding: 1.5rem !important;
          }
          
          .bg-white h1 {
            font-size: 24px !important;
            line-height: 36px !important;
          }
          
          .bg-white p {
            font-size: 13px !important;
            line-height: 18px !important;
            margin-bottom: 1.5rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default GetInTouch;
